import React, { useEffect, useState } from 'react';
import { alpha, Box, Button, Chip, Stack, Theme, Typography } from '@mui/material';
import { ElementInconsistency, Severity } from 'api/element-issues';
import useBundleTranslation from 'i18n';
import IconMi from 'components/common/icon/IconMi';
import IssueCard, { IssueCardRows } from 'components/common/issue-card/IssueCard';
import useForceExpand from 'hooks/useForceExpand';

export type IssueObjectType = 'report' | 'dataset' | 'domain';

const SEVERITY_COLOR_ALIAS: Record<Severity, 'error' | 'warning' | 'default'> = {
    High: 'error',
    Medium: 'warning',
    Low: 'default',
};

const SEVERITY_DOT_COLOR: Record<Severity, string> = {
    High: 'error.main',
    Medium: 'warning.main',
    Low: 'text.secondary',
};

function pluralize(count: number, singular: string, plural: string): string {
    return count === 1 ? `${count} ${singular}` : `${count} ${plural}`;
}

interface Props {
    inconsistencies: ElementInconsistency[];
    compact?: boolean;
    // Auto-expand on gating errors (§2.2): re-expands whenever the token changes
    forceExpandToken?: number;
    objectType?: IssueObjectType;
    // Manage Knowledge Graph (MI-29733 §5.2): without it the Resolve links and Review-all action
    // are hidden while the rest of the card still renders (spec §4.1)
    canResolve?: boolean;
    defaultExpanded?: boolean;
    floating?: boolean;
}

export default function InconsistenciesCard({
    inconsistencies,
    compact,
    forceExpandToken,
    objectType = 'report',
    canResolve = false,
    defaultExpanded = true,
    floating = false,
}: Props) {
    const { t } = useBundleTranslation('app/extreport/concierge');
    // Cards that mount already resolved start collapsed — no expand-then-collapse flicker
    const [expanded, setExpanded] = useState(() => defaultExpanded && inconsistencies.length > 0);

    useForceExpand(forceExpandToken, setExpanded);

    const allResolved = inconsistencies.length === 0;

    // Auto-collapse to `Resolved` the moment the outstanding count hits zero (spec §4.4)
    useEffect(() => {
        if (allResolved) setExpanded(false);
    }, [allResolved]);

    const handleRowActivate = (resolveUrl: string) => {
        if (!canResolve) return;
        window.open(resolveUrl, '_blank', 'noopener');
    };

    const reviewAllUrl = (() => {
        // The Measure Conflicts editor accepts one model filter — scope only when every row agrees
        const modelUids = [...new Set(inconsistencies.map((i) => i.modelUid).filter(Boolean))];
        const modelUid = modelUids.length === 1 ? (modelUids[0] as string) : null;
        return `/editor/measure-conflicts${modelUid ? `?model=${encodeURIComponent(modelUid)}` : ''}`;
    })();

    const badge = allResolved ? (
        <Chip label={t('resolved_badge')} color="success" className={'chip-mod--round-and-text'} />
    ) : (
        <Chip
            label={t('unresolved_count', { count: inconsistencies.length })}
            color="error"
            className={'chip-mod--round-and-text'}
        />
    );

    return (
        <IssueCard
            color={allResolved ? 'success' : 'warning'}
            title={t('title')}
            badge={badge}
            expanded={expanded}
            onToggle={() => setExpanded((prev) => !prev)}
            view={'concierge'}
            icon={allResolved ? 'success' : 'alert'}
            description={
                allResolved
                    ? t('description_resolved')
                    : (canResolve ? t('description', { objectType: t(`object_type_${objectType}`) }) : t('read_only_note'))
            }
            descriptionDataTest={!allResolved && !canResolve ? 'inconsistencies_read_only_note' : undefined}
            isResolved={allResolved}
            floating={floating}
        >
            <Stack
                sx={{
                    p: 1,
                    borderTop: '1px solid',
                    borderTopColor: (theme) => theme.palette.warning.main,
                    px: 1,
                    height: '100%',
                }}
            >
                <IssueCardRows view="concierge">
                    {inconsistencies.map((item, idx) => (
                        <Stack
                            key={item.measureId}
                            tabIndex={canResolve ? 0 : -1}
                            role={canResolve ? 'link' : undefined}
                            onClick={() => handleRowActivate(item.resolveUrl)}
                            onKeyDown={(e) => {
                                if (canResolve && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    // A host popup treats Enter as confirm, which closes it
                                    e.stopPropagation();
                                    handleRowActivate(item.resolveUrl);
                                }
                            }}
                            sx={{
                                p: 1,
                                pl: 2,
                                cursor: canResolve ? 'pointer' : 'default',
                                border: '1px solid',
                                borderRadius: '6px',
                                borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.16),
                                '&:hover': canResolve
                                    ? {
                                          backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.04),
                                          '& .resolve-link': { textDecoration: 'underline' },
                                      }
                                    : undefined,
                            }}
                        >
                            <Stack direction="row" alignItems="flex-start" spacing={2}>
                                <Box sx={{ fontWeight: 600, flexGrow: 1 }}>{item.measureName}</Box>
                                <Chip
                                    label={t(`severity_${item.severity.toLowerCase()}`)}
                                    color={SEVERITY_COLOR_ALIAS[item.severity]}
                                    className={'chip-mod--round-and-text'}
                                    sx={
                                        item.severity === 'Medium'
                                            ? {
                                                  color: 'rgba(190, 111, 0, 1)',
                                                  backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.24),
                                              }
                                            : undefined
                                    }
                                    data-test={`inconsistency_severity_${item.severity.toLowerCase()}`}
                                />

                                {canResolve && (
                                    <Box
                                        className="resolve-link"
                                        component="span"
                                        sx={{
                                            flexShrink: 0,
                                            whiteSpace: 'nowrap',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            pt: 0.25,
                                        }}
                                    >
                                        <IconMi icon="arrow-right" sx={{ fontSize: '16px' }} />
                                    </Box>
                                )}
                            </Stack>
                            <Stack sx={{ flexGrow: 1 }}>
                                {!compact && (
                                    <Typography
                                        sx={{
                                            color: (theme) => alpha(theme.palette.text.primary, 0.8),
                                            mt: 0.25,
                                            strong: {
                                                color: (theme) => theme.palette.text.primary,
                                            },
                                        }}
                                    >
                                        {t('inconsistent_with')}{' '}
                                        <strong>
                                            {pluralize(item.modelCount, item.modelType, item.modelType + 's')}
                                        </strong>
                                        , {t('used_by')}{' '}
                                        <strong>{t('report_count', { count: item.reportCount })}</strong>
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>
                    ))}
                </IssueCardRows>

                {canResolve && !allResolved && (
                    <Stack direction="row" justifyContent="flex-end" sx={{ pt: 2 }}>
                        <Button
                            variant="outlined"
                            color="neutral"
                            onClick={() => window.open(reviewAllUrl, '_blank', 'noopener')}
                            endIcon={<IconMi icon="arrow-right" />}
                            data-test="inconsistencies_review_all"
                        >
                            {t('review_all')}
                        </Button>
                    </Stack>
                )}
            </Stack>
        </IssueCard>
    );
}
